import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BRAND_LOGO = "/brand/byard-logo.svg";
const BRAND_ACRONYM = "/brand/byard-logo-acronym.jpg";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { sendReset } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await sendReset(email);
      setSubmitted(true);
      toast.success('If an account exists, a reset link has been sent!');
    } catch (error) {
      // Always show success to prevent email enumeration. Firebase rejects
      // unknown emails with auth/user-not-found — silence that one.
      console.warn('Password reset:', error?.code || error?.message);
      setSubmitted(true);
      toast.success('If an account exists, a reset link has been sent!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4 py-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <img 
              src={BRAND_LOGO} 
              alt="BYARD Logo" 
              className="h-24 w-auto object-contain"
              data-testid="brand-logo"
            />
          </div>
          <img 
            src={BRAND_ACRONYM} 
            alt="BYARD" 
            className="h-10 mx-auto object-contain"
            data-testid="brand-acronym"
          />
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
          {!submitted ? (
            <>
              <h2 data-testid="forgot-password-title" className="text-xl font-bold text-[#2B3A67] text-center mb-2">
                Forgot Password?
              </h2>
              <p className="text-gray-500 text-sm text-center mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-gray-400">
                    Email Address
                  </label>
                  <input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2 w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562]"
                  />
                </div>

                <button
                  data-testid="send-reset-link-button"
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl bg-[#E54562] text-white font-extrabold shadow-lg disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ph-bold ph-envelope-simple text-3xl text-green-600"></i>
              </div>
              <h2 data-testid="email-sent-title" className="text-xl font-bold text-[#2B3A67] mb-2">
                Check Your Email
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                If an account with <strong>{email}</strong> exists, you'll receive a password reset link shortly.
              </p>
              <p className="text-gray-400 text-xs mb-4">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <button
                data-testid="try-again-button"
                onClick={() => setSubmitted(false)}
                className="text-[#E54562] font-semibold text-sm hover:underline"
              >
                Try another email
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              data-testid="back-to-login-button"
              onClick={() => navigate('/auth')}
              className="text-[#2B3A67] font-semibold text-sm hover:underline flex items-center justify-center gap-2 mx-auto"
            >
              <i className="ph-bold ph-arrow-left"></i>
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
